"use client";

import { CheckIcon, XIcon } from "lucide-react";
import { cn } from "../../../lib";
import { EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS } from "./actions";

export interface CompactToggleIncludeActionsProps {
	included: boolean;
	disabled?: boolean;
	onInclude: () => void;
	onExclude: () => void;
	includeAriaLabel?: string;
	excludeAriaLabel?: string;
	className?: string;
}

const INCLUDE_ACTIVE_CLASS =
	"cursor-pointer border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-600/90";

const EXCLUDE_ACTIVE_CLASS =
	"cursor-pointer border-border bg-background text-muted-foreground hover:bg-muted";

const IDLE_BUTTON_CLASS =
	"cursor-pointer border-transparent text-muted-foreground/50 hover:bg-muted/60 hover:text-muted-foreground";

const DISABLED_BUTTON_CLASS =
	"cursor-not-allowed border-transparent text-muted-foreground/30";

/**
 * Tick / cross include chrome for extract selection drafts.
 * Prefer-over: do not invent alternate include toggles for Scene Studio extract.
 * Visual `size-5` + icon `size-3`; hit target ≥44×44 (`min-h-11 min-w-11`).
 */
export function CompactToggleIncludeActions({
	included,
	disabled = false,
	onInclude,
	onExclude,
	includeAriaLabel = "Include",
	excludeAriaLabel = "Exclude",
	className,
}: CompactToggleIncludeActionsProps) {
	return (
		<div
			className={cn("inline-flex shrink-0 items-center gap-0.5", className)}
			data-testid="compact-toggle-include-actions"
		>
			<button
				type="button"
				aria-label={includeAriaLabel}
				aria-pressed={included}
				disabled={disabled}
				data-testid="compact-toggle-include"
				onClick={(e) => {
					e.stopPropagation();
					if (!disabled) {
						onInclude();
					}
				}}
				className={cn(
					EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS,
					"size-5 min-h-11 min-w-11",
					disabled
						? DISABLED_BUTTON_CLASS
						: included
							? INCLUDE_ACTIVE_CLASS
							: IDLE_BUTTON_CLASS,
				)}
			>
				<CheckIcon className="size-3" aria-hidden />
			</button>
			<button
				type="button"
				aria-label={excludeAriaLabel}
				aria-pressed={!included}
				disabled={disabled}
				data-testid="compact-toggle-exclude"
				onClick={(e) => {
					e.stopPropagation();
					if (!disabled) {
						onExclude();
					}
				}}
				className={cn(
					EDITABLE_FIELD_ACTION_BUTTON_BASE_CLASS,
					"size-5 min-h-11 min-w-11",
					disabled
						? DISABLED_BUTTON_CLASS
						: included
							? EXCLUDE_ACTIVE_CLASS
							: IDLE_BUTTON_CLASS,
				)}
			>
				<XIcon className="size-3" aria-hidden />
			</button>
		</div>
	);
}
